import React, { useState, useEffect, useRef } from "react";

import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import CloseIcon from "@material-ui/icons/Close";
import { Stack } from "@mui/material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useStyles = makeStyles(() => ({
  root: { display: "flex", flexWrap: "wrap" },

  dialogPaper: {
    borderRadius: 14,
    border: "1px solid rgba(17, 24, 39, 0.08)",
    boxShadow: "0 18px 60px rgba(16, 24, 40, 0.18)",
    overflow: "hidden"
  },

  dialogTitle: {
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },

  titleWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 2
  },

  title: {
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont",
    color: "#111827"
  },

  subtitle: {
    fontSize: 12.5,
    color: "#6B7280"
  },

  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid rgba(17, 24, 39, 0.08)"
  },

  dialogContent: {
    padding: 20,
    background:
      "linear-gradient(180deg, rgba(249,250,251,0.6) 0%, rgba(255,255,255,1) 100%)",
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#fff"
    }
  },

  dialogActions: {
    padding: "14px 20px",
    borderTop: "1px solid rgba(17, 24, 39, 0.06)",
    gap: 10
  },

  primaryButton: {
    borderRadius: 12,
    textTransform: "none",
    boxShadow: "0 6px 18px rgba(59, 130, 246, 0.25)"
  },

  secondaryButton: {
    borderRadius: 12,
    textTransform: "none"
  }
}));

const FlowBuilderTicketModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setQueueSelected] = useState();
  const [routingMode, setRoutingMode] = useState("specialist");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open === "edit" || open === "create") {
      (async () => {
        try {
          setLoading(true);
          const { data: response } = await api.get("/queue");
          setQueues(response);
          if (open === "edit") {
            const queue = response.find(item => item.id === data.data.id);
            if (queue) setQueueSelected(queue.id);
            setRoutingMode(data.data.routingMode || "human");
          } else {
            setQueueSelected();
            setRoutingMode("specialist");
          }
          setActiveModal(true);
        } catch (error) {
          console.log(error);
        } finally {
          setLoading(false);
        }
      })();
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = () => {
    if (!selectedQueue) {
      return toast.error("Adicione uma fila");
    }

    const queue = queues.find(item => item.id === selectedQueue);
    if (routingMode === "specialist" && !queue?.promptId) {
      return toast.error("A fila selecionada precisa ter um Agente de IA configurado");
    }
    const nodeData = { ...queue, routingMode };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: nodeData
      });
    } else if (open === "create") {
      onSave({ data: nodeData });
    }

    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.titleWrapper}>
            <Typography className={classes.title}>
              {open === "create" ? "Adicionar fila ao fluxo" : "Editar fila"}
            </Typography>
            <Typography className={classes.subtitle}>
              Escolha se a fila continuará com um especialista de IA ou aguardará uma pessoa
            </Typography>
          </div>

          <IconButton
            onClick={handleClose}
            className={classes.closeButton}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent} dividers>
          {loading ? (
            <Stack alignItems="center" padding={2}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <Stack spacing={2}>
              <FormControl component="fieldset">
                <RadioGroup
                  value={routingMode}
                  onChange={event => setRoutingMode(event.target.value)}
                >
                  <FormControlLabel
                    value="specialist"
                    control={<Radio color="primary" />}
                    label="Continuar com o Agente de IA da fila"
                  />
                  <FormControlLabel
                    value="human"
                    control={<Radio color="primary" />}
                    label="Transferir para atendimento humano"
                  />
                </RadioGroup>
                <FormHelperText>
                  A transferência humana pausa a IA até o atendimento ser retomado.
                </FormHelperText>
              </FormControl>

              <FormControl
                error={
                  routingMode === "specialist" &&
                  Boolean(selectedQueue) &&
                  !queues.find(queue => queue.id === selectedQueue)?.promptId
                }
              >
                <Select
                  value={selectedQueue || ""}
                  onChange={event => setQueueSelected(event.target.value)}
                  fullWidth
                  variant="outlined"
                  displayEmpty
                  renderValue={value => {
                    if (!value) return "Selecione uma fila";
                    const queue = queues.find(item => item.id === value);
                    return queue ? queue.name : "";
                  }}
                >
                  {queues.map(queue => (
                    <MenuItem key={queue.id} value={queue.id}>
                      {queue.name}
                      {routingMode === "specialist" && !queue.promptId
                        ? " — sem agente configurado"
                        : ""}
                    </MenuItem>
                  ))}
                </Select>
                {routingMode === "specialist" &&
                  selectedQueue &&
                  !queues.find(queue => queue.id === selectedQueue)?.promptId && (
                    <FormHelperText>
                      Configure um Agente de IA nesta fila antes de usar o roteamento
                      especialista.
                    </FormHelperText>
                  )}
              </FormControl>
            </Stack>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            variant="outlined"
            className={classes.secondaryButton}
          >
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSaveContact}
            color="primary"
            variant="contained"
            className={classes.primaryButton}
            disabled={
              loading ||
              !selectedQueue ||
              (routingMode === "specialist" &&
                !queues.find(queue => queue.id === selectedQueue)?.promptId)
            }
          >
            {open === "create" ? "Adicionar" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderTicketModal;
